import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { db } from '../../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Eye, GripVertical, Trash2, Plus, Save, RotateCcw, Monitor, Users, Settings, Maximize2, Minimize2 } from 'lucide-react';
import { defaultDashboardConfigs, widgetLibrary } from '../../config/dashboardConfig';
import StatsOverviewWidget from '../widgets/StatsOverviewWidget';
import StudentSearchWidget from '../widgets/StudentSearchWidget';
import MenteeListWidget from '../widgets/MenteeListWidget';
import AttendanceCardWidget from '../widgets/AttendanceCardWidget';
import AchievementApprovalsWidget from '../widgets/AchievementApprovalsWidget';
import LeaveApprovalsWidget from '../widgets/LeaveApprovalsWidget';
import MessagingWidget from '../widgets/MessagingWidget';
import StaffManagementWidget from '../widgets/StaffManagementWidget';
import FacultyRolesWidget from '../widgets/FacultyRolesWidget';
import AnalyticsChartsWidget from '../widgets/AnalyticsChartsWidget';
import SheetsSyncWidget from '../widgets/SheetsSyncWidget';
import AttendanceEntryWidget from '../widgets/AttendanceEntryWidget';
import BatchAssignmentsWidget from '../widgets/BatchAssignmentsWidget';
import MentorOverviewWidget from '../widgets/MentorOverviewWidget';
import UnassignedStudentsWidget from '../widgets/UnassignedStudentsWidget';
import DivisionAnalyticsWidget from '../widgets/DivisionAnalyticsWidget';
import MenteeDetailsWidget from '../widgets/MenteeDetailsWidget';
import AttendanceAlertsWidget from '../widgets/AttendanceAlertsWidget';
import StudentProfileWidget from '../widgets/StudentProfileWidget';
import IaTest1CardWidget from '../widgets/IaTest1CardWidget';
import IaTest2CardWidget from '../widgets/IaTest2CardWidget';
import QuickActionsWidget from '../widgets/QuickActionsWidget';
import AchievementsListWidget from '../widgets/AchievementsListWidget';
import LeaderboardWidget from '../widgets/LeaderboardWidget';
import NotificationsWidget from '../widgets/NotificationsWidget';
import StudentIdCardWidget from '../widgets/StudentIdCardWidget';
import WidgetMaker from '../widgets/WidgetMaker';
import StudentListWidget from '../widgets/StudentListWidget';
import MenteeYaadiWidget from '../widgets/MenteeYaadiWidget';
import StudentManagementWidget from '../widgets/StudentManagementWidget';
import SemesterSubjectsWidget from '../widgets/SemesterSubjectsWidget';
import ParentsMeetManagementWidget from '../widgets/ParentsMeetManagementWidget';
import CourseMarksEntryWidget from '../widgets/CourseMarksEntryWidget';
import CourseAttendanceEntryWidget from '../widgets/CourseAttendanceEntryWidget';
import FacultyProfileWidget from '../widgets/FacultyProfileWidget';
import SystemArchiveWidget from '../widgets/SystemArchiveWidget';
import SubmissionStatusWidget from '../widgets/SubmissionStatusWidget';
import NoticesAndAnnouncementsWidget from '../widgets/NoticesAndAnnouncementsWidget';
import WarningLettersWidget from '../widgets/WarningLettersWidget';
import ForminfoWidget from '../widgets/ForminfoWidget';
import DivisionAssignmentsWidget from '../widgets/DivisionAssignmentsWidget';
import PendingAchievementsWidget from '../widgets/PendingAchievementsWidget';
import AttendanceReportWidget from '../widgets/AttendanceReportWidget';
import TopperformersWidget from '../widgets/TopperformersWidget';
import NanoMessagingWidget from '../widgets/NanoMessagingWidget';
import SlowAverageFastLearnersWidget from '../widgets/SlowAverageFastLearnersWidget';

const previewRoleLabels = {
  hod: 'HOD',
  mentoringCoordinator: 'Mentoring Co-ordinator',
  classCoordinator: 'Class Co-ordinator',
  mentor: 'Mentor',
  courseIncharge: 'Subject Incharge',
  student: 'Student'
};

const getRoleInitial = (role) => ({
  student: 'ST',
  mentor: 'M',
  classCoordinator: 'CC',
  mentoringCoordinator: 'MC',
  courseIncharge: 'SI',
  hod: 'HOD'
}[role] || 'DB');

const previewRequiredWidgetsByRole = {
  hod: [
    { id: 'faculty-roles', enabled: true, order: 3, size: 'full' },
    { id: 'student-management', enabled: true, order: 4, size: 'full' },
    { id: 'semester-subjects', enabled: true, order: 5, size: 'full' },
    { id: 'divisionassignments', enabled: true, order: 6, size: 'full' },
    { id: 'system-archive', enabled: true, order: 7, size: 'full' },
    { id: 'submission-status-report', enabled: true, order: 8, size: 'full' }
  ],
  classCoordinator: [
    { id: 'faculty-profile', enabled: true, order: 1, size: 'full' },
    { id: 'attendance-entry', enabled: true, order: 3, size: 'full' },
    { id: 'submission-status-report', enabled: true, order: 6, size: 'full' }
  ],
  mentoringCoordinator: [
    { id: 'faculty-profile', enabled: true, order: 1, size: 'full' },
    { id: 'submission-status-report', enabled: true, order: 7, size: 'full' }
  ],
  mentor: [
    { id: 'faculty-profile', enabled: true, order: 1, size: 'full' },
    { id: 'parents-meet-management', enabled: true, order: 6, size: 'full' },
    { id: 'submission-status-report', enabled: true, order: 7, size: 'full' }
  ],
  courseIncharge: [
    { id: 'faculty-profile', enabled: true, order: 1, size: 'full' },
    { id: 'course-marks-entry', enabled: true, order: 2, size: 'full' },
    { id: 'course-attendance-entry', enabled: true, order: 3, size: 'full' }
  ]
};

const previewPriorityWidgets = [
  'attendance-entry',
  'course-marks-entry',
  'course-attendance-entry',
  'achievement-approvals',
  'leave-approvals'
];

const previewStudents = [
  { id: 'FA101', rollNumber: 'FA101', firstName: 'Sample', surname: 'Student', name: 'Sample Student', division: 'A', batch: 'A1', gender: 'Male' },
  { id: 'FA102', rollNumber: 'FA102', firstName: 'Demo', surname: 'Learner', name: 'Demo Learner', division: 'A', batch: 'A1', gender: 'Female' }
];

const getPreviewUser = (roleName) => {
  const base = {
    id: 'preview-user',
    email: 'preview@ybit.local',
    username: 'preview',
    loginId: 'preview',
    name: previewRoleLabels[roleName] || 'Preview User',
    role: roleName,
    assignedDivision: 'A',
    assignedBatch: 'A1',
    userType: roleName === 'student' ? 'student' : 'faculty'
  };

  if (roleName === 'student') {
    return {
      ...previewStudents[0],
      role: 'student',
      email: 'student@ybit.local'
    };
  }

  if (roleName === 'courseIncharge') {
    const assignment = {
      role: 'courseIncharge',
      label: 'Course Management',
      semester: 1,
      subjectId: 'AM-I',
      subjectCode: 'AM-I',
      subjectName: 'Applied Mathematics-I',
      teachingDivision: 'A',
      division: 'A',
      scope: 'course',
      courseInchargeEmail: base.email,
      courseInchargeName: base.name
    };

    return {
      ...base,
      activeAssignment: assignment,
      activeCourseAssignment: assignment
    };
  }

  return base;
};

const previewDashboardData = {
  stats: {
    totalStudents: 2,
    totalStaff: 2,
    avgAttendance: 82,
    atRisk: 1,
    totalAchievements: 1,
    totalMentees: 2
  },
  allStudents: previewStudents,
  allStaff: [
    { id: 'faculty-1', name: 'Prof. Sample Faculty', email: 'faculty@ybit.local', role: 'mentor' }
  ],
  mentees: previewStudents,
  divisionStudents: previewStudents,
  pendingAchievements: [
    { id: 'achievement-1', rollNumber: 'FA101', studentName: 'Sample Student', title: 'Technical Quiz', status: 'pending' }
  ],
  pendingLeaves: [
    { id: 'leave-1', rollNumber: 'FA102', studentName: 'Demo Learner', reason: 'Medical', status: 'pending' }
  ],
  attendance: { overallPercentage: 82, theoryPercentage: 80, practicalPercentage: 85 },
  iaTest1: { total: 42, maxMarks: 50 },
  iaTest2: { total: 44, maxMarks: 50 },
  achievements: [
    { id: 'achievement-2', title: 'Workshop Participation', status: 'approved' }
  ],
  mentorName: 'Prof. Sample Mentor',
  coordinatorName: 'Prof. Class Co-ordinator'
};

const mergeRequiredWidgets = (role, config) => {
  const requiredWidgets = previewRequiredWidgetsByRole[role] || [];
  const baseConfig = config?.widgets ? config : (defaultDashboardConfigs[role] || { widgets: [], permissions: {} });
  const existingIds = new Set((baseConfig.widgets || []).map((widget) => widget.id));
  const missingWidgets = requiredWidgets.filter((widget) => !existingIds.has(widget.id));

  return {
    ...baseConfig,
    widgets: [...(baseConfig.widgets || []), ...missingWidgets]
  };
};

const sanitizeDashboardConfig = (role, config) => {
  const mergedConfig = mergeRequiredWidgets(role, config);
  const normalizedWidgets = (mergedConfig.widgets || [])
    .map((widget, index) => ({
      ...widget,
      enabled: widget.enabled !== undefined ? widget.enabled : true,
      order: widget.order !== undefined ? widget.order : index + 1,
      size: widget.size || 'full'
    }))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return {
    ...mergedConfig,
    widgets: role === 'courseIncharge'
      ? normalizedWidgets
      : normalizedWidgets.filter((widget) => !['course-marks-entry', 'course-attendance-entry'].includes(widget.id))
  };
};

const getPreviewWidget = (widgetId, roleName) => {
  const activeUser = getPreviewUser(roleName);
  const userRole = roleName;
  const widgetProps = {
    canViewDashboard: true,
    userRole,
    assignedDivision: activeUser?.assignedDivision,
    assignedBatch: activeUser?.assignedBatch
  };

  switch (widgetId) {
    case 'stats-overview':
      return { title: 'Overview Statistics', content: <StatsOverviewWidget stats={previewDashboardData.stats} /> };
    case 'student-search':
      return { title: 'Student Search', content: <StudentSearchWidget onSearchStudent={async () => null} {...widgetProps} /> };
    case 'mentee-list':
      return { title: 'My Mentees', content: <MenteeListWidget mentees={previewDashboardData.mentees} /> };
    case 'attendance-card':
      return { title: 'My Attendance', content: <AttendanceCardWidget attendance={previewDashboardData.attendance} /> };
    case 'achievement-approvals':
      return { title: 'Pending Achievements', content: <AchievementApprovalsWidget achievements={previewDashboardData.pendingAchievements} userEmail={activeUser?.email} {...widgetProps} /> };
    case 'leave-approvals':
      return { title: 'Pending Leave Requests', content: <LeaveApprovalsWidget leaves={previewDashboardData.pendingLeaves} {...widgetProps} /> };
    case 'messaging':
      return { title: 'Messaging', content: <MessagingWidget userRole={userRole} /> };
    case 'staff-management':
      return { title: 'Staff Management', content: <StaffManagementWidget allStaff={previewDashboardData.allStaff} {...widgetProps} /> };
    case 'faculty-roles':
      return { title: 'Faculty Management', content: <FacultyRolesWidget /> };
    case 'faculty-profile':
      return { title: 'My Faculty Profile', content: <FacultyProfileWidget userData={activeUser} /> };
    case 'system-archive':
      return { title: 'Backup, Reset & Annual Analysis', content: <SystemArchiveWidget /> };
    case 'student-management':
      return { title: 'Student Management', content: <StudentManagementWidget /> };
    case 'semester-subjects':
      return { title: 'Semester Subject Management', content: <SemesterSubjectsWidget /> };
    case 'analytics-charts':
      return { title: 'Analytics', content: <AnalyticsChartsWidget stats={previewDashboardData.stats} /> };
    case 'sheets-sync':
      return { title: 'Google Sheets Sync', content: <SheetsSyncWidget /> };
    case 'attendance-entry':
      return { title: 'Attendance Entry', content: <AttendanceEntryWidget assignedDivision={activeUser?.assignedDivision} userName={activeUser?.name} /> };
    case 'course-marks-entry':
      return {
        title: 'Course Marks Entry',
        content: <CourseMarksEntryWidget userEmail={activeUser?.email} userName={activeUser?.name} userId={activeUser?.id} username={activeUser?.username || activeUser?.loginId} assignedDivision={activeUser?.assignedDivision} courseAssignment={activeUser?.activeCourseAssignment} />
      };
    case 'course-attendance-entry':
      return {
        title: 'Course Attendance Entry',
        content: <CourseAttendanceEntryWidget userEmail={activeUser?.email} userName={activeUser?.name} userId={activeUser?.id} username={activeUser?.username || activeUser?.loginId} assignedDivision={activeUser?.assignedDivision} courseAssignment={activeUser?.activeCourseAssignment} />
      };
    case 'submission-status-report':
      return {
        title: 'Submission Status Reports',
        content: <SubmissionStatusWidget preview userRole={userRole} assignedDivision={activeUser?.assignedDivision} assignedBatch={activeUser?.assignedBatch} userName={activeUser?.name} />
      };
    case 'batch-assignments':
      return { title: 'Batch Management', content: <BatchAssignmentsWidget /> };
    case 'mentor-overview':
      return { title: 'Mentor Overview', content: <MentorOverviewWidget allStaff={previewDashboardData.allStaff} /> };
    case 'unassigned-students':
      return { title: 'Unassigned Students', content: <UnassignedStudentsWidget /> };
    case 'division-analytics':
      return { title: 'Division Analytics', content: <DivisionAnalyticsWidget stats={previewDashboardData.stats} /> };
    case 'mentee-details':
      return { title: 'Mentee Details', content: <MenteeDetailsWidget /> };
    case 'attendance-alerts':
      return { title: 'Attendance Alerts', content: <AttendanceAlertsWidget mentees={previewDashboardData.mentees} /> };
    case 'parents-meet-management':
      return { title: 'Parents Meet Management', content: <ParentsMeetManagementWidget assignedBatch={activeUser?.assignedBatch} userEmail={activeUser?.email} userName={activeUser?.name} /> };
    case 'student-profile':
      return { title: 'Profile', content: <StudentProfileWidget userData={activeUser} /> };
    case 'ia-test1-card':
      return { title: 'IA Test 1', content: <IaTest1CardWidget iaTest1={previewDashboardData.iaTest1} /> };
    case 'ia-test2-card':
      return { title: 'IA Test 2', content: <IaTest2CardWidget iaTest2={previewDashboardData.iaTest2} /> };
    case 'quick-actions':
      return { title: 'Quick Actions', content: <QuickActionsWidget rollNumber={activeUser?.rollNumber} /> };
    case 'achievements-list':
      return { title: 'My Achievements', content: <AchievementsListWidget achievements={previewDashboardData.achievements} /> };
    case 'leaderboard':
      return { title: 'Leaderboard', content: <LeaderboardWidget /> };
    case 'notifications':
      return { title: 'Notifications', content: <NotificationsWidget /> };
    case 'student-id-card':
      return { title: 'Student ID', content: <StudentIdCardWidget userData={activeUser} mentorName={previewDashboardData.mentorName} coordinatorName={previewDashboardData.coordinatorName} /> };
    case 'widget-maker':
      return { title: 'Widget Maker', content: <WidgetMaker /> };
    case 'studentlist':
      return { title: 'Student List', content: <StudentListWidget /> };
    case 'noticesandannouncements':
      return { title: 'NoticesAndAnnouncements', content: <NoticesAndAnnouncementsWidget userRole={activeUser.role} userEmail={activeUser.email} userName={activeUser.name} /> };
    case 'warningletters':
      return { title: 'WarningLetters', content: <WarningLettersWidget userRole={activeUser?.role} userDivision={activeUser?.assignedDivision} userName={activeUser?.name} /> };
    case 'forminfo':
      return { title: 'Forminfo', content: <ForminfoWidget /> };
    case 'divisionassignments':
      return { title: 'Division Management', content: <DivisionAssignmentsWidget /> };
    case 'pendingachievements':
      return { title: 'PendingAchievements', content: <PendingAchievementsWidget /> };
    case 'attendancereport':
      return { title: 'AttendanceReport', content: <AttendanceReportWidget /> };
    case 'topperformers':
      return { title: 'Topperformers', content: <TopperformersWidget /> };
    case 'nanomessaging':
      return { title: 'NanoMessaging', content: <NanoMessagingWidget /> };
    case 'slowaveragefastlearners':
      return { title: 'SlowAverageFastLearners', content: <SlowAverageFastLearnersWidget /> };
    case 'mentee-yaadi':
      return { title: 'My Mentees', content: <MenteeYaadiWidget userEmail={activeUser?.email} userName={activeUser?.name} /> };
    default:
      return {
        title: widgetLibrary[widgetId]?.name || widgetId,
        content: (
          <div className="py-8 text-center text-gray-400">
            Widget "{widgetLibrary[widgetId]?.name || widgetId}" is not implemented in the live renderer yet.
          </div>
        )
      };
  }
};

const RealWidgetPreviewBody = ({ widget, roleName }) => {
  const [expanded, setExpanded] = useState(false);
  const widgetInfo = widgetLibrary[widget.id] || {};
  const previewWidget = getPreviewWidget(widget.id, roleName);
  const isPriority = previewPriorityWidgets.includes(widget.id);

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        className="flex w-full items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 text-left hover:from-blue-100 hover:to-indigo-100"
      >
        <span>
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            {previewWidget.title}
            {isPriority && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-600">Priority</span>}
          </span>
          <span className="mt-1 block text-xs text-gray-500">{widgetInfo.description || 'Live dashboard widget'}</span>
        </span>
        <span className="text-xs font-semibold text-blue-700">{expanded ? 'Hide' : 'Load preview'}</span>
      </button>

      {expanded ? (
        <div className="relative">
          <div className="max-h-[420px] overflow-auto p-4 pointer-events-none">
            {previewWidget.content}
          </div>
          <div className="border-t border-blue-100 bg-blue-50 px-4 py-2 text-xs text-blue-700">
            Read-only preview. Use the actual role dashboard for data entry.
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 px-4 py-3 text-xs text-gray-500">
          Click to load the real widget preview. Kept collapsed to keep this control center light.
        </div>
      )}
    </div>
  );
};

// Enhanced Sortable Widget Item for Preview Area
const SortablePreviewWidget = ({ widget, roleName, onRemove, onSizeChange, isPreviewMode = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const widgetInfo = widgetLibrary[widget.id] || {};

  const getGridClass = (size) => {
    switch(size) {
      case 'full': return 'col-span-12';
      case 'half': return 'col-span-6';
      case 'third': return 'col-span-4';
      default: return 'col-span-12';
    }
  };

  // Get widget preview content based on type
  const getWidgetPreview = () => {
    return <RealWidgetPreviewBody widget={widget} roleName={roleName} />;
  };

  /*
    const category = widgetInfo.category || '';
    
    if (category.includes('Analytics') || category.includes('Statistics')) {
      return (
        <div className="h-32 bg-gradient-to-br from-blue-50 to-indigo-100 rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="h-3 w-20 bg-blue-300 rounded"></div>
            <div className="text-2xl">📊</div>
          </div>
          <div className="h-8 bg-blue-200 rounded mb-2"></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="h-3 bg-blue-300 rounded"></div>
            <div className="h-3 bg-blue-300 rounded"></div>
            <div className="h-3 bg-blue-300 rounded"></div>
          </div>
        </div>
      );
    } else if (category.includes('Academic')) {
      return (
        <div className="h-32 bg-gradient-to-br from-green-50 to-emerald-100 rounded p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="h-3 w-24 bg-green-300 rounded"></div>
            <div className="text-xl">🎓</div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-green-200 rounded w-full"></div>
            <div className="h-4 bg-green-200 rounded w-3/4"></div>
            <div className="h-4 bg-green-200 rounded w-1/2"></div>
          </div>
        </div>
      );
    } else if (category.includes('Performance')) {
      return (
        <div className="h-32 bg-gradient-to-br from-purple-50 to-pink-100 rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="h-3 w-20 bg-purple-300 rounded"></div>
            <div className="text-2xl">📈</div>
          </div>
          <div className="flex items-end justify-between h-16 gap-1">
            <div className="w-full bg-purple-300 rounded-t" style={{height: '40%'}}></div>
            <div className="w-full bg-purple-400 rounded-t" style={{height: '70%'}}></div>
            <div className="w-full bg-purple-300 rounded-t" style={{height: '55%'}}></div>
            <div className="w-full bg-purple-500 rounded-t" style={{height: '90%'}}></div>
          </div>
        </div>
      );
    } else if (category.includes('Attendance')) {
      return (
        <div className="h-32 bg-gradient-to-br from-orange-50 to-yellow-100 rounded p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="h-3 w-28 bg-orange-300 rounded"></div>
            <div className="text-xl">📅</div>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {[...Array(14)].map((_, i) => (
              <div key={i} className={`h-4 rounded ${i % 3 === 0 ? 'bg-orange-300' : 'bg-orange-200'}`}></div>
            ))}
          </div>
        </div>
      );
    } else {
      return (
        <div className="h-32 bg-gradient-to-br from-gray-50 to-slate-100 rounded p-3 flex items-center justify-center">
          <div className="text-center">
            <div className="text-3xl mb-2">📦</div>
            <div className="h-2 w-20 bg-gray-300 rounded mx-auto"></div>
          </div>
        </div>
      );
    }
  };
  */

  if (isPreviewMode) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`${getGridClass(widget.size)} group relative`}
      >
        <div className={`bg-white rounded-lg border-2 shadow-sm h-full transition-all duration-200 ${
          isDragging 
            ? 'border-blue-500 shadow-xl scale-105' 
            : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
        }`}>
          {/* Drag Handle - Top Bar */}
          <div 
            {...listeners}
            {...attributes}
            className="flex items-center justify-between p-3 border-b border-gray-100 cursor-grab active:cursor-grabbing bg-gray-50 rounded-t-lg"
          >
            <div className="flex items-center space-x-2 flex-1">
              <GripVertical className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
              <h4 className="font-semibold text-sm text-gray-800 truncate">
                {widgetInfo.name || widget.id}
              </h4>
            </div>
            
            {/* Controls */}
            <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
              <select
                value={widget.size}
                onChange={(e) => onSizeChange(widget.id, e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
              >
                <option value="full">Full</option>
                <option value="half">Half</option>
                <option value="third">Third</option>
              </select>
              
              <button
                onClick={() => onRemove(widget.id)}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Remove widget"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Widget Content Preview */}
          <div className="p-4">
            {getWidgetPreview()}
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs text-gray-500">{widgetInfo.category}</span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                {widget.size === 'full' ? '100%' : widget.size === 'half' ? '50%' : '33%'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List mode (for left panel)
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border-2 rounded-lg p-4 mb-3 transition-all ${
        isDragging ? 'border-blue-500 shadow-lg' : 'border-gray-200 hover:border-blue-300'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1">
          <button
            {...listeners}
            {...attributes}
            className="cursor-grab active:cursor-grabbing hover:bg-gray-100 p-2 rounded"
          >
            <GripVertical className="w-5 h-5 text-gray-400" />
          </button>
          
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900">{widgetInfo.name || widget.id}</h4>
            <p className="text-xs text-gray-500">{widgetInfo.description || 'Custom widget'}</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <select
            value={widget.size}
            onChange={(e) => onSizeChange(widget.id, e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1"
            onClick={(e) => e.stopPropagation()}
          >
            <option value="full">Full Width</option>
            <option value="half">Half Width</option>
            <option value="third">Third Width</option>
          </select>

          <button
            onClick={() => onRemove(widget.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Enhanced Dashboard Preview with Drag & Drop
const InteractiveDashboardPreview = ({ widgets, roleName, onRemove, onSizeChange, onDragEnd }) => {
  const [activeId, setActiveId] = useState(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getRoleIcon = (role) => {
    const iconLabels = {
      student: 'ST',
      mentor: 'M',
      classCoordinator: 'CC',
      mentoringCoordinator: 'MC',
      courseIncharge: 'SI',
      hod: 'HOD'
    };
    if (iconLabels[role]) return iconLabels[role];

    switch(role) {
      case 'student': return '🎓';
      case 'mentor': return '👨‍🏫';
      case 'classCoordinator': return '📋';
      case 'mentoringCoordinator': return '🎯';
      case 'hod': return '👔';
      default: return '👤';
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEndLocal = (event) => {
    setActiveId(null);
    if (onDragEnd) {
      onDragEnd(event);
    }
  };

  return (
    <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200 shadow-inner">
      {/* Preview Header */}
      <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50 text-sm font-bold text-blue-700">{getRoleIcon(roleName)}</span>
            <div>
              <h3 className="text-lg font-bold text-gray-900 capitalize">
                {previewRoleLabels[roleName] || roleName.replace(/([A-Z])/g, ' $1').trim()} Dashboard
              </h3>
              <p className="text-xs text-gray-500">Actual dashboard preview - drag to reorder, resize, or remove</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="text-right">
              <p className="text-xs text-gray-500">Widgets</p>
              <p className="text-lg font-bold text-purple-600">{widgets.length}</p>
            </div>
            <Monitor className="w-8 h-8 text-purple-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Dashboard Grid with Drag & Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEndLocal}
      >
        <div className="bg-white rounded-lg p-6 min-h-96 shadow-sm border border-gray-200">
          {widgets.length === 0 ? (
            <div className="text-center py-20">
              <Monitor className="w-20 h-20 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-400 text-lg">No widgets configured</p>
              <p className="text-gray-400 text-sm mt-2">Add widgets to see the preview</p>
            </div>
          ) : (
            <SortableContext items={widgets.map(w => w.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-12 gap-4">
                {widgets.map((widget) => (
                  <SortablePreviewWidget
                    key={widget.id}
                    widget={widget}
                    roleName={roleName}
                    onRemove={onRemove}
                    onSizeChange={onSizeChange}
                    isPreviewMode={true}
                  />
                ))}
              </div>
            </SortableContext>
          )}
        </div>

        <DragOverlay>
          {activeId ? (
            <div className="bg-white rounded-lg shadow-2xl border-2 border-blue-500 p-4 opacity-90">
              <div className="font-semibold text-gray-900">
                {widgetLibrary[activeId]?.name || activeId}
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Preview Footer */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center space-x-2">
          <div className="text-blue-600">💡</div>
          <p className="text-xs text-blue-800">
            <strong>Interactive Preview:</strong> Drag widgets directly in the preview to reorder them. 
            Click controls to resize or remove widgets.
          </p>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Configurator Component
const DashboardConfigurator = () => {
  const [selectedRole, setSelectedRole] = useState('hod');
  const [roleConfigs, setRoleConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [viewMode, setViewMode] = useState('split'); // 'split', 'preview', 'list'
  const [hasChanges, setHasChanges] = useState(false);

  const roles = [
    { id: 'courseIncharge', name: 'Subject Incharge', icon: 'SI' },
    { id: 'hod', name: 'HOD', icon: '👔' },
    { id: 'mentoringCoordinator', name: 'Mentoring Coordinator', icon: '🎯' },
    { id: 'classCoordinator', name: 'Class Coordinator', icon: '📋' },
    { id: 'mentor', name: 'Mentor', icon: '👨‍🏫' },
    { id: 'student', name: 'Student', icon: '🎓' },
  ];

  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    try {
      setLoading(true);

      const configs = {};
      for (const role of roles) {
        const docRef = doc(db, 'dashboardConfigs', role.id);
        const docSnap = await getDoc(docRef);
        const savedConfig = docSnap.exists() ? docSnap.data() : defaultDashboardConfigs[role.id];
        configs[role.id] = sanitizeDashboardConfig(role.id, savedConfig);
      }

      setRoleConfigs(configs);
      setLoading(false);
    } catch (error) {
      console.error('Error loading configurations:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const widgets = Object.entries(widgetLibrary)
      .filter(([id, info]) => {
        return info.availableFor && info.availableFor.includes(selectedRole);
      })
      .map(([id, info]) => ({
        id,
        name: info.name,
        description: info.description,
        category: info.category
      }));

    setAvailableWidgets(widgets);
  }, [selectedRole]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setRoleConfigs(prev => {
        const config = prev[selectedRole];
        const oldIndex = config.widgets.findIndex(w => w.id === active.id);
        const newIndex = config.widgets.findIndex(w => w.id === over.id);
        
        const reorderedWidgets = arrayMove(config.widgets, oldIndex, newIndex);
        
        // Update order property for all widgets
        const widgetsWithOrder = reorderedWidgets.map((widget, index) => ({
          ...widget,
          order: index + 1
        }));
        
        return {
          ...prev,
          [selectedRole]: {
            ...config,
            widgets: widgetsWithOrder
          }
        };
      });
      setHasChanges(true);
    }
  };

  const handleAddWidget = (widgetId) => {
    setRoleConfigs(prev => {
      const currentWidgets = prev[selectedRole].widgets;
      const newOrder = currentWidgets.length > 0 
        ? Math.max(...currentWidgets.map(w => w.order || 0)) + 1 
        : 1;
      
      return {
        ...prev,
        [selectedRole]: {
          ...prev[selectedRole],
          widgets: [...currentWidgets, { 
            id: widgetId, 
            size: 'full',
            enabled: true,
            order: newOrder
          }]
        }
      };
    });
    setHasChanges(true);
  };

  const handleRemoveWidget = (widgetId) => {
    setRoleConfigs(prev => ({
      ...prev,
      [selectedRole]: {
        ...prev[selectedRole],
        widgets: prev[selectedRole].widgets.filter(w => w.id !== widgetId)
      }
    }));
    setHasChanges(true);
  };

  const handleSizeChange = (widgetId, newSize) => {
    setRoleConfigs(prev => ({
      ...prev,
      [selectedRole]: {
        ...prev[selectedRole],
        widgets: prev[selectedRole].widgets.map(w =>
          w.id === widgetId ? { ...w, size: newSize } : w
        )
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const docRef = doc(db, 'dashboardConfigs', selectedRole);
      await setDoc(docRef, roleConfigs[selectedRole]);
      window.alert('✅ Configuration saved successfully!');
      setHasChanges(false);
      setSaving(false);
    } catch (error) {
      console.error('Error saving configuration:', error);
      window.alert('❌ Failed to save configuration');
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      for (const role of roles) {
        const docRef = doc(db, 'dashboardConfigs', role.id);
        if (roleConfigs[role.id]) {
          await setDoc(docRef, roleConfigs[role.id]);
        }
      }
      window.alert('✅ All dashboard configurations saved successfully!');
      setHasChanges(false);
      setSaving(false);
    } catch (error) {
      console.error('Error saving all configurations:', error);
      window.alert('❌ Failed to save configurations');
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Reset configuration to last saved state?')) {
      await loadConfigurations();
      setHasChanges(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard configurations...</p>
        </div>
      </div>
    );
  }

  const currentConfig = roleConfigs[selectedRole] || { widgets: [] };
  const currentWidgets = [...(currentConfig.widgets || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const orderedRoles = ['hod', 'mentoringCoordinator', 'classCoordinator', 'mentor', 'courseIncharge', 'student']
    .map((roleId) => roles.find((role) => role.id === roleId))
    .filter(Boolean);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard Configurator</h2>
          <p className="text-gray-600 mt-1">
            Interactive drag & drop configuration with actual dashboard preview
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {/* View Mode Toggle */}
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('split')}
              className={`px-3 py-2 rounded text-sm flex items-center space-x-1 ${
                viewMode === 'split' ? 'bg-white shadow text-purple-700' : 'text-gray-600'
              }`}
              title="Split View"
            >
              <Settings className="w-4 h-4" />
              <span>Split</span>
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={`px-3 py-2 rounded text-sm flex items-center space-x-1 ${
                viewMode === 'preview' ? 'bg-white shadow text-purple-700' : 'text-gray-600'
              }`}
              title="Preview Only"
            >
              <Maximize2 className="w-4 h-4" />
              <span>Preview</span>
            </button>
          </div>

          {hasChanges && (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleReset}
                className="flex items-center space-x-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Reset</span>
              </button>

              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>

              <button
                onClick={handleSaveAll}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save All'}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Role Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Role to Configure:</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {orderedRoles.map((role) => (
            <button
              key={role.id}
              onClick={() => setSelectedRole(role.id)}
              className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                selectedRole === role.id
                  ? 'border-purple-500 bg-purple-50 shadow-md'
                  : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
              }`}
            >
              <span className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-xs font-bold text-gray-700">
                {getRoleInitial(role.id)}
              </span>
              <span className={`text-sm font-medium ${
                selectedRole === role.id ? 'text-purple-700' : 'text-gray-700'
              }`}>
                {previewRoleLabels[role.id] || role.name}
              </span>
              <span className="text-xs text-gray-500 mt-1">
                {roleConfigs[role.id]?.widgets.length || 0} widgets
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      {viewMode === 'split' ? (
        <div className="grid grid-cols-12 gap-6">
          {/* Left: Available Widgets */}
          <div className="col-span-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Available Widgets
                <span className="text-sm text-gray-500 ml-2">({availableWidgets.length})</span>
              </h3>
              
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                {availableWidgets.map((widget) => {
                  const isAdded = currentWidgets.find(w => w.id === widget.id);
                  return (
                    <button
                      key={widget.id}
                      onClick={() => !isAdded && handleAddWidget(widget.id)}
                      disabled={isAdded}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        isAdded
                          ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                          : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50 cursor-pointer hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm text-gray-900">{widget.name}</p>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{widget.description}</p>
                          <span className="inline-block mt-2 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {widget.category}
                          </span>
                        </div>
                        {!isAdded ? (
                          <Plus className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
                        ) : (
                          <span className="text-xs text-green-600 font-medium ml-2">✓ Added</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: Interactive Preview */}
          <div className="col-span-8">
            <InteractiveDashboardPreview
              widgets={currentWidgets}
              roleName={selectedRole}
              onRemove={handleRemoveWidget}
              onSizeChange={handleSizeChange}
              onDragEnd={handleDragEnd}
            />
          </div>
        </div>
      ) : (
        /* Full Width Preview */
        <InteractiveDashboardPreview
          widgets={currentWidgets}
          roleName={selectedRole}
          onRemove={handleRemoveWidget}
          onSizeChange={handleSizeChange}
          onDragEnd={handleDragEnd}
        />
      )}

      {/* Tips */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
          <span className="mr-2">💡</span> Interactive Configuration Tips:
        </h4>
        <div className="grid grid-cols-2 gap-4 text-sm text-blue-800">
          <ul className="space-y-1">
            <li>• <strong>Add widgets:</strong> Click on available widgets to add them</li>
            <li>• <strong>Reorder:</strong> Drag widgets directly in the preview area</li>
            <li>• <strong>Resize:</strong> Use the dropdown to change widget width</li>
          </ul>
          <ul className="space-y-1">
            <li>• <strong>Remove:</strong> Click the trash icon on any widget</li>
            <li>• <strong>View modes:</strong> Toggle between split and full preview</li>
            <li>• <strong>Save:</strong> Changes are saved only when you click Save</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DashboardConfigurator;
